import { Link } from '@inertiajs/react';
import { Card } from '@/components/ui/card';
import { Star } from 'lucide-react';
import { Book } from '@/types/models';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';

interface BookCardProps {
    book: Book;
    translatedSlug?: string;
}

export function BookCard({ book, translatedSlug }: BookCardProps) {
    const { bookUrl } = useTranslatedRoutes();
    
    return (
        <Link
            href={bookUrl(translatedSlug || book.slug)}
            className="group block"
        >
            <Card className="h-full hover:shadow-md transition-all duration-200 overflow-hidden p-0 gap-0">
                {/* Book Cover - 2:3 aspect ratio */}
                <div className="relative aspect-[2/3] bg-muted">
                    {(book.cover_image_ai || book.cover_image) ? (
                        <img
                            src={book.cover_image_ai || book.cover_image}
                            alt={book.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full p-4 bg-gradient-to-br from-muted to-muted/50">
                            <p className="text-xs text-muted-foreground text-center line-clamp-3">
                                {book.title}
                            </p>
                        </div>
                    )}
                </div>
                
                {/* Book Info */}
                <div className="p-3 space-y-1">
                    {/* Title */}
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                        {book.title}
                    </h3>
                    
                    {/* Author */}
                    <p className="text-xs text-muted-foreground line-clamp-1">
                        {typeof book.author === 'string' ? book.author : book.author?.name}
                    </p>
                    
                    {/* Rating */}
                    {book.rating_value && (
                        <div className="flex items-center gap-1">
                            <Star className="h-3 w-3 fill-accent text-accent" />
                            <span className="text-xs font-medium">{book.rating_value}</span>
                            {book.rating_count && (
                                <span className="text-xs text-muted-foreground">
                                    ({book.rating_count})
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </Card>
        </Link>
    );
}